// The bank's settings (RFC-056 D3, D4, D7): what a user sees of items not
// theirs, whether a requesting instructor may have a paper's hidden part,
// what grading may spend in a month, and where the Claude credential
// grading spends is kept — a reference, never the credential. One form as
// wide as the page (byj-1wv.51.18), a row per setting — its name and what it
// does beside its control, under one another on a narrow page — and Save at
// its foot; the catalog's two as radios, whose words a select would cut.
import { useEffect, useState, type ReactNode } from "react";
import type { BankClient, BankSettings as Settings } from "../bank";
import { t, whenMinute } from "../i18n";
import { Choices } from "./Choices";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

export function BankSettings({ client }: { client: BankClient }) {
  const [s, setS] = useState<Settings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    client.settings().then(setS, (err: unknown) => setError(errText(err)));
  }, [client]);
  if (!s) return error ? <p className="error">{t("common:error", { m: error })}</p> : <p className="muted">{t("common:loading")}</p>;
  const save = async () => {
    setBusy(true);
    setDone(false);
    try {
      setS(
        await client.setSettings({
          catalog: s.catalog,
          hidden_release: s.hidden_release,
          monthly_budget_usd: s.monthly_budget_usd,
          claude_credential: s.claude_credential,
        }),
      );
      setError(null);
      setDone(true);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };
  // a value changed, "saved" no longer says it
  const set = (x: Partial<Settings>) => {
    setDone(false);
    setS({ ...s, ...x });
  };
  return (
    <div data-testid="bank-settings">
      <p className="muted small panel-lead">{t("bank:settingsLead")}</p>
      <form
        className="card bank-settings"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <Setting name={t("bank:settingsCatalog")} id="bank-set-catalog">
          <Choices<Settings["catalog"]>
            name="bank-catalog"
            className="bank-choice"
            legend={t("bank:settingsCatalog")}
            legendShown={false}
            value={s.catalog}
            onChange={(catalog) => set({ catalog })}
            options={[
              { value: "metadata", name: t("bank:catalogMetadataName"), what: t("bank:catalogMetadata") },
              { value: "public", name: t("bank:catalogPublicName"), what: t("bank:catalogPublic") },
            ]}
          />
        </Setting>
        <Setting name={t("bank:settingsHiddenReleaseName")} what={t("bank:settingsHiddenReleaseNote")} id="bank-set-hidden">
          <label className="toggle">
            <input type="checkbox" checked={s.hidden_release} onChange={(e) => set({ hidden_release: e.target.checked })} />
            {t("bank:settingsHiddenRelease")}
          </label>
        </Setting>
        <Setting name={t("bank:settingsBudget")} what={t("bank:settingsBudgetNote")} id="bank-set-budget">
          <span className="row num-field">
            <span className="muted small">$</span>
            <input
              type="number"
              min={0}
              step={10}
              inputMode="decimal"
              value={s.monthly_budget_usd}
              onChange={(e) => set({ monthly_budget_usd: Number(e.target.value) })}
              aria-label={t("bank:settingsBudget")}
            />
            <span className="muted small">{t("bank:perMonth")}</span>
          </span>
        </Setting>
        <Setting name={t("bank:settingsCredential")} what={t("bank:settingsCredentialNote")} id="bank-set-credential">
          <input value={s.claude_credential} onChange={(e) => set({ claude_credential: e.target.value })} placeholder="ssm:/byj-exam/claude" aria-label={t("bank:settingsCredential")} />
        </Setting>
        <div className="row wrap bank-settings-foot">
          {error && <p className="error flush grow">{t("common:error", { m: error })}</p>}
          {done && (
            <p className="ok-text flush" role="status">
              {t("bank:settingsSaved")}
            </p>
          )}
          {s.updated_at && !s.updated_at.startsWith("0001-") && <span className="muted small">{t("bank:settingsUpdated", { w: whenMinute(s.updated_at) })}</span>}
          <span className="grow" />
          <button className="primary" type="submit" disabled={busy}>
            {t("bank:settingsSave")}
          </button>
        </div>
      </form>
    </div>
  );
}

/** A setting's row: its name and what it does on the left, its control on the right; one above the other where the page is narrow. */
function Setting({ name, what, id, children }: { name: string; what?: string; id: string; children: ReactNode }) {
  return (
    <section className="bank-setting" aria-labelledby={id}>
      <div className="bank-setting-what">
        <h3 id={id} className="flush">
          {name}
        </h3>
        {what && <p className="muted small flush">{what}</p>}
      </div>
      <div className="bank-setting-control">{children}</div>
    </section>
  );
}
